import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { API_PATHS } from '@/constants/api-paths'
import { useAuthStore } from '@/stores/auth-store'
import { buildLoginRedirectPath, persistAuthRedirectContext } from './auth-redirect'
import {
  resolveScopedPostForServiceInstance,
  resolveActiveServiceInstanceId,
  SERVICE_INSTANCE_PARAM,
} from './service-instance'

// Use the API URL from environment, or empty string for mock mode
const API_URL = import.meta.env.VITE_API_URL || ''
const API_REGION = import.meta.env.VITE_API_REGION || 'uk'
const API_ORGANIZATION = import.meta.env.VITE_API_ORGANIZATION || 'eln'
const API_BASE_URL_STORAGE_KEY = 'selected-api-base-url'
const API_BASE_URL_CHANGE_EVENT = 'selected-api-base-url-change'
const API_BASE_LOCKED = (() => {
  const lockFlag = String(import.meta.env.VITE_LOCK_API_BASE ?? '').trim().toLowerCase()
  if (lockFlag === 'true') return true
  if (lockFlag === 'false') return false
  // Default: lock only for production deploy/mode (staging keeps backend selection).
  const deployEnv = String(import.meta.env.VITE_APP_ENV || import.meta.env.MODE || '').trim().toLowerCase()
  return deployEnv === 'production'
})()

function normalizeApiBaseUrl(value: unknown): string {
  return String(value ?? '').trim().replace(/\/+$/, '')
}

function readStoredApiBaseUrl(): string {
  if (API_BASE_LOCKED || typeof window === 'undefined') {
    return ''
  }

  try {
    return normalizeApiBaseUrl(window.localStorage.getItem(API_BASE_URL_STORAGE_KEY))
  } catch {
    return ''
  }
}

function clearStoredApiBaseUrl(): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.removeItem(API_BASE_URL_STORAGE_KEY)
  } catch {
    // Ignore storage write failures.
  }
}

function resolveInitialApiBaseUrl(): string {
  const envBaseUrl = normalizeApiBaseUrl(API_URL)
  if (API_BASE_LOCKED) {
    // Production / locked builds always use the baked --api-url value.
    clearStoredApiBaseUrl()
    return envBaseUrl
  }

  // Prefer environment configuration over persisted UI selection to avoid
  // stale localStorage endpoints hijacking active dev backend routing.
  return envBaseUrl || readStoredApiBaseUrl()
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: resolveInitialApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
})

// Export the instance for the mock setup to access
export const api = axiosInstance

export function isApiBaseUrlLocked(): boolean {
  return API_BASE_LOCKED
}

export function getDefaultApiBaseUrl(): string {
  return normalizeApiBaseUrl(API_URL)
}

export function getApiBaseUrl(): string {
  if (API_BASE_LOCKED) {
    return getDefaultApiBaseUrl() || normalizeApiBaseUrl(axiosInstance.defaults.baseURL)
  }
  return normalizeApiBaseUrl(axiosInstance.defaults.baseURL) || readStoredApiBaseUrl() || getDefaultApiBaseUrl()
}

export function setApiBaseUrl(url: string): string {
  if (API_BASE_LOCKED) {
    const lockedBaseUrl = getDefaultApiBaseUrl()
    axiosInstance.defaults.baseURL = lockedBaseUrl
    clearStoredApiBaseUrl()
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(API_BASE_URL_CHANGE_EVENT, { detail: { url: lockedBaseUrl } }))
    }
    return lockedBaseUrl
  }

  const nextBaseUrl = normalizeApiBaseUrl(url) || getDefaultApiBaseUrl()

  axiosInstance.defaults.baseURL = nextBaseUrl

  if (typeof window !== 'undefined') {
    try {
      if (nextBaseUrl && nextBaseUrl !== getDefaultApiBaseUrl()) {
        window.localStorage.setItem(API_BASE_URL_STORAGE_KEY, nextBaseUrl)
      } else {
        window.localStorage.removeItem(API_BASE_URL_STORAGE_KEY)
      }
    } catch {
      // Ignore storage write failures and continue with the in-memory selection.
    }

    window.dispatchEvent(new CustomEvent(API_BASE_URL_CHANGE_EVENT, { detail: { url: nextBaseUrl } }))
  }

  return nextBaseUrl
}

export function subscribeToApiBaseUrl(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== API_BASE_URL_STORAGE_KEY) {
      return
    }

    axiosInstance.defaults.baseURL = resolveInitialApiBaseUrl()
    listener()
  }

  const handleCustomChange = () => {
    listener()
  }

  window.addEventListener('storage', handleStorage)
  window.addEventListener(API_BASE_URL_CHANGE_EVENT, handleCustomChange)

  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(API_BASE_URL_CHANGE_EVENT, handleCustomChange)
  }
}

export type AppRequestConfig = AxiosRequestConfig & {
  skipAuthRedirectOn401?: boolean
  skipPostIdHeader?: boolean
  skipServiceInstanceId?: boolean
}

type AuthStoreSnapshot = ReturnType<typeof useAuthStore.getState>

function readPersistedAuthSnapshot(): Partial<AuthStoreSnapshot> {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw) as { state?: Partial<AuthStoreSnapshot> } | Partial<AuthStoreSnapshot>
    const state = (parsed && typeof parsed === 'object' && 'state' in parsed
      ? parsed.state
      : parsed) as Partial<AuthStoreSnapshot> | undefined
    return state && typeof state === 'object' ? state : {}
  } catch {
    return {}
  }
}

function getHeaderReadyAuthState(snapshot: AuthStoreSnapshot): AuthStoreSnapshot {
  const persisted = readPersistedAuthSnapshot()
  if (!persisted || Object.keys(persisted).length === 0) {
    return snapshot
  }

  return {
    ...snapshot,
    accessToken: snapshot.accessToken || persisted.accessToken || null,
    sessionId: snapshot.sessionId || persisted.sessionId || null,
    serviceInstanceId: snapshot.serviceInstanceId || persisted.serviceInstanceId || null,
    currentPost: snapshot.currentPost || persisted.currentPost || null,
    availablePosts: (snapshot.availablePosts && snapshot.availablePosts.length > 0)
      ? snapshot.availablePosts
      : (persisted.availablePosts || []),
    currentBranch: snapshot.currentBranch || persisted.currentBranch || null,
    availableBranches: (snapshot.availableBranches && snapshot.availableBranches.length > 0)
      ? snapshot.availableBranches
      : (persisted.availableBranches || []),
    isAuthenticated: snapshot.isAuthenticated || Boolean(persisted.isAuthenticated),
  }
}

/** Trims and drops empty; ensures header values are strings (axios may stringify oddly otherwise). */
function headerString(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null
  }
  const s = String(value).trim()
  return s.length > 0 ? s : null
}

function normalizeOrganizationHeader(value: unknown): string | null {
  const source = headerString(value)
  if (!source) {
    return null
  }

  // Backend expects a normalized organization key (for example: `eln-org`).
  return source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || null
}

function resolveOrganizationHeader(
  authState: AuthStoreSnapshot,
  resolvedServiceInstanceId: string | null,
  requestUrl?: string,
  requestMethod?: string,
): string | null {
  const organizationFromCurrentPost = normalizeOrganizationHeader(authState.currentPost?.org_name)
  if (organizationFromCurrentPost) {
    return organizationFromCurrentPost
  }

  if (resolvedServiceInstanceId) {
    const normalizedRequestUrl = String(requestUrl ?? '').toLowerCase()
    const normalizedRequestMethod = String(requestMethod ?? 'get').trim().toLowerCase()
    const isRoleManagementRequest = normalizedRequestUrl.includes('/org/roles/')
      || normalizedRequestUrl.includes('/org/policy/')
    const isWriteRequest = ['post', 'put', 'patch', 'delete'].includes(normalizedRequestMethod)
    const matchedPost = resolveScopedPostForServiceInstance({
      currentPost: authState.currentPost,
      availablePosts: authState.availablePosts,
      serviceInstanceId: resolvedServiceInstanceId,
      preferMostCapable: isRoleManagementRequest || isWriteRequest,
      includeOrganizationScopeFallback: true,
    })

    const organizationFromMatchedPost = normalizeOrganizationHeader(matchedPost?.org_name)
    if (organizationFromMatchedPost) {
      return organizationFromMatchedPost
    }
  }

  for (const post of authState.availablePosts ?? []) {
    const candidate = normalizeOrganizationHeader(post?.org_name)
    if (candidate) {
      return candidate
    }
  }

  return normalizeOrganizationHeader(API_ORGANIZATION)
}

/**
 * `post-id` must be the encrypted token from login / GetByToken (`posts[].id`), not a raw DB id.
 * The backend's `post_decryption` uses the same Fernet key as the session; a wrong value raises
 * "Invalid Id or Session Token". Prefer the current post, else a post whose service instance
 * matches the active `service_instance_id` in the URL / store.
 */
function resolvePostIdForHeaders(
  authState: AuthStoreSnapshot,
  resolvedServiceInstanceId: string | null,
  requestUrl?: string,
  requestMethod?: string,
): string | null {
  const normalizeIdentityValue = (value: unknown): string | null => {
    if (value === undefined || value === null) {
      return null
    }

    const normalizedValue = String(value).trim()
    return normalizedValue.length > 0 ? normalizedValue : null
  }

  const getBranchIdentityCandidates = (branch: {
    id?: string | number | null
    instanceId?: string | null
    serviceId?: string | number | null
    code?: string | null
  } | null | undefined): string[] => {
    if (!branch) {
      return []
    }

    const candidates = [
      branch.instanceId,
      branch.serviceId,
      branch.code,
      branch.id,
    ]
      .map((value) => normalizeIdentityValue(value))
      .filter((value): value is string => Boolean(value))

    return Array.from(new Set(candidates))
  }

  const branchIdentityCandidates = [
    ...getBranchIdentityCandidates(authState.currentBranch as any),
    ...(authState.availableBranches ?? []).flatMap((branch) => getBranchIdentityCandidates(branch as any)),
  ]

  const branchIdentitySet = new Set(branchIdentityCandidates)

  const collectPostServiceInstanceCandidates = (post: {
    service_instance?: string | number | null
    service_instance_id?: string | number | null
  } | null | undefined): string[] => {
    if (!post) {
      return []
    }

    const directCandidates = [
      post.service_instance,
      post.service_instance_id,
    ]
      .map((value) => normalizeIdentityValue(value))
      .filter((value): value is string => Boolean(value))

    if (directCandidates.length === 0) {
      return []
    }

    const allCandidates = new Set<string>(directCandidates)
    const matchingBranch = (authState.availableBranches ?? []).find((branch) => {
      const identities = getBranchIdentityCandidates(branch as any)
      return identities.some((identity) => allCandidates.has(identity))
    })

    if (matchingBranch) {
      for (const identity of getBranchIdentityCandidates(matchingBranch as any)) {
        allCandidates.add(identity)
      }
    }

    for (const candidate of directCandidates) {
      if (branchIdentitySet.has(candidate)) {
        allCandidates.add(candidate)
      }
    }

    return Array.from(allCandidates)
  }

  const postMatchesServiceInstance = (
    post: {
      service_instance?: string | number | null
      service_instance_id?: string | number | null
    } | null | undefined,
    serviceInstanceId: string,
  ): boolean => {
    const target = normalizeIdentityValue(serviceInstanceId)
    if (!target) {
      return false
    }

    const candidates = collectPostServiceInstanceCandidates(post)
    return candidates.includes(target)
  }

  const normalizedRequestUrl = String(requestUrl ?? '').toLowerCase()
  const normalizedRequestMethod = String(requestMethod ?? 'get').trim().toLowerCase()
  const isRoleManagementRequest = normalizedRequestUrl.includes('/org/roles/')
    || normalizedRequestUrl.includes('/org/policy/')
  const isWorkerManagementRequest = normalizedRequestUrl.includes('/worker/')
    || normalizedRequestUrl.includes('/workerskill/')
    || normalizedRequestUrl.includes('/workerpayscale/')
    || normalizedRequestUrl.includes('/job/worker/')
  const isWriteRequest = ['post', 'put', 'patch', 'delete'].includes(normalizedRequestMethod)
  const preferMostCapable = isRoleManagementRequest || isWorkerManagementRequest || isWriteRequest

  const currentPost = authState.currentPost
  const posts = authState.availablePosts ?? []
  const currentPostId = headerString(currentPost?.id)
  const currentPostIsInAvailablePosts = Boolean(
    currentPostId
    && posts.some((post) => {
      const availablePostId = headerString(post?.id)
      if (availablePostId && availablePostId === currentPostId) {
        return true
      }

      const currentPostDbId = headerString(currentPost?._id)
      const availablePostDbId = headerString(post?._id)
      return Boolean(currentPostDbId && availablePostDbId && currentPostDbId === availablePostDbId)
    }),
  )
  const trustedCurrentPostId = currentPostIsInAvailablePosts ? currentPostId : null
  const currentPostServiceInstance = currentPost?.service_instance ?? currentPost?.service_instance_id

  if (resolvedServiceInstanceId) {
    const target = String(resolvedServiceInstanceId)

    if (trustedCurrentPostId && currentPostServiceInstance != null && postMatchesServiceInstance(currentPost, target) && !preferMostCapable) {
      return trustedCurrentPostId
    }

    const exactMatchingPosts = posts.filter((post) => postMatchesServiceInstance(post, target))
    if (exactMatchingPosts.length > 0) {
      const scopedMatch = resolveScopedPostForServiceInstance({
        currentPost: currentPost && postMatchesServiceInstance(currentPost, target) ? currentPost : null,
        availablePosts: exactMatchingPosts,
        serviceInstanceId: target,
        preferMostCapable,
        includeOrganizationScopeFallback: false,
      })

      const scopedMatchId = headerString(scopedMatch?.id)
      if (scopedMatchId) {
        return scopedMatchId
      }
    }

    const match = resolveScopedPostForServiceInstance({
      currentPost,
      availablePosts: authState.availablePosts,
      serviceInstanceId: target,
      preferMostCapable,
      // Never auto-fallback to org/root scope for headers; keep requests bound
      // to the selected post or explicit same-service matches.
      includeOrganizationScopeFallback: false,
    })

    if (match?.id != null) {
      const matchId = headerString(match.id)
      if (matchId) {
        return matchId
      }
    }

    if (trustedCurrentPostId && currentPostServiceInstance != null && postMatchesServiceInstance(currentPost, target)) {
      return trustedCurrentPostId
    }
  }

  if (trustedCurrentPostId) {
    return trustedCurrentPostId
  }

  if (posts.length === 0) {
    return null
  }

  return headerString(posts[0]?.id)
}

function resolveExplicitServiceInstanceId(config: InternalAxiosRequestConfig): string | null {
  const existingHeader = headerString((config.headers as Record<string, unknown> | undefined)?.['service-instance-id'])
  if (existingHeader) {
    return existingHeader
  }

  if (config.params instanceof URLSearchParams) {
    return headerString(config.params.get(SERVICE_INSTANCE_PARAM))
  }

  if (config.params && typeof config.params === 'object') {
    return headerString((config.params as Record<string, unknown>)[SERVICE_INSTANCE_PARAM])
  }

  if (config.data instanceof FormData) {
    const value = config.data.get(SERVICE_INSTANCE_PARAM)
    const normalized = typeof value === 'string' ? headerString(value) : null
    if (normalized) {
      return normalized
    }
  }

  const resolveFromPayload = (payload: unknown): string | null => {
    if (!payload) {
      return null
    }

    if (Array.isArray(payload)) {
      for (const entry of payload) {
        const candidate = resolveFromPayload(entry)
        if (candidate) {
          return candidate
        }
      }
      return null
    }

    if (typeof payload === 'object') {
      return headerString((payload as Record<string, unknown>)[SERVICE_INSTANCE_PARAM])
    }

    return null
  }

  if (typeof config.data === 'string') {
    try {
      const parsed = JSON.parse(config.data)
      const candidate = resolveFromPayload(parsed)
      if (candidate) {
        return candidate
      }
    } catch {
      // ignore non-JSON string payloads
    }
  } else {
    const candidate = resolveFromPayload(config.data)
    if (candidate) {
      return candidate
    }
  }

  return null
}

function clearMultipartContentTypeHeader(config: InternalAxiosRequestConfig): void {
  const headers = config.headers as unknown as {
    delete?: (name: string) => void
    ['Content-Type']?: string
    ['content-type']?: string
  }

  // Axios may normalize header casing differently based on runtime/version.
  if (typeof headers?.delete === 'function') {
    headers.delete('Content-Type')
    headers.delete('content-type')
  }

  if (headers && typeof headers === 'object') {
    delete headers['Content-Type']
    delete headers['content-type']
  }
}

// Also set up interceptors on the base axios for mock support
export function setupApiInterceptors(instance: AxiosInstance = axiosInstance) {
  const SESSION_401_HINTS = [
    'session expired',
    'session has expired',
    'token expired',
    'jwt expired',
    'invalid signature',
    'not authenticated',
    'authentication credentials were not provided',
    'please log in to continue',
    'please login to continue',
  ]

  const get401PayloadText = (value: unknown): string => {
    if (!value) {
      return ''
    }

    if (typeof value === 'string') {
      return value.toLowerCase()
    }

    if (typeof value === 'object') {
      return JSON.stringify(value).toLowerCase()
    }

    return String(value).toLowerCase()
  }

  const isSessionExpired401 = (error: AxiosError): boolean => {
    const payloadText = get401PayloadText(error.response?.data)

    if (!payloadText) {
      return false
    }

    return SESSION_401_HINTS.some((hint) => payloadText.includes(hint))
  }

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (config.data instanceof FormData) {
        clearMultipartContentTypeHeader(config)
        // Prevent browser lockups from indefinitely pending upload requests.
        if (!config.timeout || config.timeout <= 0) {
          config.timeout = 45_000
        }
      }

      const authState = getHeaderReadyAuthState(useAuthStore.getState())
      const token = headerString(authState.accessToken)
      if (token) {
        const bearer = `Bearer ${token}`
        config.headers.Authorization = bearer
        config.headers['X-Authorization'] = bearer
      }

      const sessionHeader = headerString(authState.sessionId)
      if (sessionHeader) {
        config.headers['session-id'] = sessionHeader
      }

      if (!config.headers.region) {
        config.headers.region = API_REGION
      }

      const skipServiceInstanceId = (config as AppRequestConfig).skipServiceInstanceId === true
      const explicitServiceInstanceId = skipServiceInstanceId ? null : resolveExplicitServiceInstanceId(config)
      const serviceInstanceId = skipServiceInstanceId
        ? null
        : explicitServiceInstanceId || headerString(
            resolveActiveServiceInstanceId({
              search: window.location.search,
              post: authState.currentPost,
              currentBranch: authState.currentBranch,
              availableBranches: authState.availableBranches,
              storedServiceInstanceId: authState.serviceInstanceId,
              token: authState.accessToken,
            }),
          )

      if (config.url?.includes('/Store/Product/Variant/')) {
        console.log('[API DEBUG] Resolving service instance for variant request:', {
          url: config.url,
          method: config.method,
          skipServiceInstanceId,
          explicitServiceInstanceId,
          serviceInstanceId,
          search: window.location.search,
          currentPostServiceInstance: authState.currentPost?.service_instance ?? authState.currentPost?.service_instance_id,
          currentBranchInstanceId: authState.currentBranch?.instanceId,
          storedServiceInstanceId: authState.serviceInstanceId,
        })
      }

      if (!config.headers.organization) {
        const resolvedOrganizationHeader = resolveOrganizationHeader(
          authState,
          serviceInstanceId,
          config.url,
          config.method,
        )
        if (resolvedOrganizationHeader) {
          config.headers.organization = resolvedOrganizationHeader
        }
      }

      const skipPostIdHeader = (config as AppRequestConfig).skipPostIdHeader === true
      if (skipPostIdHeader) {
        delete (config.headers as Record<string, unknown>)['post-id']
        delete (config.headers as Record<string, unknown>)['Post-Id']
      }
      const existingPostIdHeader = headerString((config.headers as any)?.['post-id'])
      if (!skipPostIdHeader && !existingPostIdHeader) {
        const postId = resolvePostIdForHeaders(authState, serviceInstanceId, config.url, config.method)
        if (postId) {
          config.headers['post-id'] = postId
        }
      }

      if (serviceInstanceId) {
        const existingServiceInstanceHeader = headerString((config.headers as Record<string, unknown> | undefined)?.['service-instance-id'])
        if (!existingServiceInstanceHeader) {
          config.headers['service-instance-id'] = serviceInstanceId
        }

        if (config.params instanceof URLSearchParams) {
          if (!config.params.has(SERVICE_INSTANCE_PARAM)) {
            config.params.append(SERVICE_INSTANCE_PARAM, serviceInstanceId)
          }
        } else {
          const params = { ...(config.params as Record<string, unknown> | undefined) }
          if (!(SERVICE_INSTANCE_PARAM in params)) {
            params[SERVICE_INSTANCE_PARAM] = serviceInstanceId
          }
          config.params = params
        }
        
        if (config.url?.includes('/Store/Product/Variant/')) {
          console.log('[API DEBUG] Variant request with service-instance-id:', {
            url: config.url,
            method: config.method,
            serviceInstanceId,
            hasHeader: !!config.headers['service-instance-id']
          })
        }
      } else {
        if (config.url?.includes('/Store/Product/Variant/')) {
          console.log('[API DEBUG] Variant request WITHOUT service-instance-id:', {
            url: config.url,
            method: config.method,
            serviceInstanceId,
            authServiceInstanceId: authState.serviceInstanceId
          })
        }
      }

      const branchId = authState.currentBranch?.id
      if (branchId != null && String(branchId).trim() !== '') {
        config.headers['X-Branch-ID'] = String(branchId)
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor to handle token refresh
  instance.interceptors.response.use(
    (response) => {
      const refreshedSessionId = headerString(response.headers['x-session-id'])
      if (refreshedSessionId) {
        const currentSessionId = useAuthStore.getState().sessionId
        if (currentSessionId !== refreshedSessionId) {
          useAuthStore.getState().setSessionId(refreshedSessionId)
        }
      }
      return response
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as (InternalAxiosRequestConfig & {
        _retry?: boolean
        skipAuthRedirectOn401?: boolean
      }) | undefined

      if (error.response?.status === 401 && !originalRequest?._retry) {
        const authState = useAuthStore.getState()

        if (!authState.hasHydrated) {
          return Promise.reject(error)
        }

        const isLoginRequest = originalRequest?.url?.includes(API_PATHS.auth.login)
        const shouldSkipRedirect = originalRequest?.skipAuthRedirectOn401 === true

          // "invalid id or session token" is often raised for stale/incorrect post-id header
          // and should not hard-logout the user. Keep forced reauth only for true session-expiry 401s.
          const shouldForceReauthForRequest = isSessionExpired401(error)

        if (!isLoginRequest && !shouldSkipRedirect && shouldForceReauthForRequest) {
          const from = `${window.location.pathname}${window.location.search}`
         let tenantServiceInstanceId = ''

try {
  const authStorage = localStorage.getItem('auth-storage')

  if (authStorage) {
    const parsed = JSON.parse(authStorage)
    const state = parsed?.state ?? parsed

    const tenantPost = state?.availablePosts?.find(
      (post: any) =>
        post?.level_variant_unique_id === 'head-tenant-lvl-variant' ||
        post?.service_variant_type === 'TENANT' ||
        post?.service_variant_name === 'TENANT'
    )

    tenantServiceInstanceId =
      tenantPost?.service_instance_id ?? ''
  }
} catch (e) {
  console.error('Failed to resolve tenant service instance', e)
}

          persistAuthRedirectContext({
            from,
            reason: 'session-expired',
          })

          authState.logout()

          if (window.location.pathname !== '/login') {
            window.location.href = buildLoginRedirectPath(tenantServiceInstanceId, 'session-expired', from)
          }
        }
      }

      return Promise.reject(error)
    }
  )
}

// Set up interceptors immediately
setupApiInterceptors()

function getStatusFallbackMessage(status?: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to perform this action.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 424) return 'The request could not be completed because a required dependency failed.'
  if (status && status >= 500) {
    return `The server returned an unexpected error (HTTP ${status}).`
  }
  return 'The request could not be completed.'
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function formatOverlapDateTime(value: string, options: Intl.DateTimeFormatOptions): string {
  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(undefined, options).format(parsed)
}

function humanizeWorkdayOverlapMessage(message: string): string | null {
  const match = message.match(
    /^Worker already has another workday entry that overlaps with this time slot \(entry #([^,]+),\s*([^,]+),\s*([^,]+),\s*([^) ]+)\s+to\s+([^)]+)\)\.?$/i,
  )
  if (!match) {
    return null
  }

  const [, entryId, jobTitle, workDate, start, end] = match
  const dateLabel = formatOverlapDateTime(`${workDate.trim()}T12:00:00`, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const startLabel = formatOverlapDateTime(start.trim(), {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
  const endLabel = formatOverlapDateTime(end.trim(), {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })

  return `This worker already has work scheduled for ${jobTitle.trim()} on ${dateLabel}, ${startLabel} - ${endLabel} (entry #${entryId.trim()}). Choose a different time or edit that existing entry.`
}

/**
 * Extract a human-readable message from org / DRF / proxy response bodies.
 */
function extractMessageFromResponseData(data: unknown): string | null {
  if (data == null) {
    return null
  }
  if (typeof data === 'string') {
    const t = data.trim()
    return t.length > 0 ? t : null
  }
  if (Array.isArray(data)) {
    const messages = data
      .map((item) => extractMessageFromResponseData(item))
      .filter((message): message is string => Boolean(message))
    return messages.length > 0 ? messages.join('; ') : null
  }
  if (!isPlainObject(data)) {
    return null
  }

  const pick = (v: unknown): string | null => {
    if (typeof v === 'string' && v.trim().length > 0) {
      return v.trim()
    }
    return null
  }

  const nestedDataMessage = extractMessageFromResponseData(data.data)
  const direct = pick(data.message)
  const moreInfo = pick(data.more_info) ?? (typeof data.more_info === 'number' ? String(data.more_info) : null)
  const isGenericWrapperMessage = direct != null && /^(workflow\s+status\s+update\s+failed|request\s+failed|operation\s+failed|invalid\s+input|failed|error)\.?$/i.test(direct)
  if (nestedDataMessage && (!direct || isGenericWrapperMessage)) {
    return nestedDataMessage
  }

  if (moreInfo && (!direct || isGenericWrapperMessage)) {
    return moreInfo
  }

  const topLevel =
    direct
    ?? pick(data.detail)
    ?? (typeof data.error === 'string' ? pick(data.error) : null)
  if (topLevel) {
    return topLevel
  }

  if (isPlainObject(data.error) && pick(data.error.message)) {
    return pick(data.error.message)
  }

  if (nestedDataMessage) {
    return nestedDataMessage
  }

  if (data.non_field_errors != null) {
    const n = data.non_field_errors
    if (Array.isArray(n) && n.length > 0) {
      return n.map(String).join(', ')
    }
    if (typeof n === 'string' && n.trim()) {
      return n.trim()
    }
  }

  if (moreInfo) {
    return moreInfo
  }

  // DRF field errors: { "field": ["msg1", "msg2"] } or { "field": "msg" }
  const reserved = new Set([
    'detail', 'message', 'error', 'status', 'data', 'more_info', 'code', 'non_field_errors',
  ])
  const parts: string[] = []
  for (const [key, value] of Object.entries(data)) {
    if (reserved.has(key)) {
      continue
    }
    if (Array.isArray(value) && value.length > 0) {
      parts.push(`${key}: ${value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')}`)
    } else if (value != null && typeof value !== 'object') {
      parts.push(`${key}: ${String(value)}`)
    }
  }
  if (parts.length > 0) {
    return parts.join('; ')
  }

  return null
}

function humanizeValidationMessage(message: string): string {
  const overlapMessage = humanizeWorkdayOverlapMessage(message)
  if (overlapMessage) {
    return overlapMessage
  }

  const refundLimitMatch = message.match(
    /requested_amount:\s*([0-9.]+)\s*;\s*remaining_refundable_amount:\s*([0-9.]+)/i,
  )
  if (refundLimitMatch) {
    const [, requestedAmount, remainingAmount] = refundLimitMatch
    return `Requested refund amount ${requestedAmount} exceeds the remaining refundable balance of ${remainingAmount}.`
  }

  const replacements: Array<[string, string]> = [
    [
      'planned_start and planned_end must be provided together.',
      'Enter both a planned start and planned end time, or leave both empty.',
    ],
    [
      'Selected worker skill is inactive.',
      'This worker skill is inactive. Choose an active skill or use the one linked to this worker\'s assignment.',
    ],
    [
      'worker_skill must be active.',
      'This worker skill is inactive. Choose an active skill or use the one linked to this worker\'s assignment.',
    ],
    [
      'planned_start must be before or equal to planned_end.',
      'Planned start must be on or before the planned end time.',
    ],
    [
      'check_in must be before or equal to check_out.',
      'Check-in must be on or before check-out.',
    ],
    [
      'A record with this value already exists. Please check your input and try again.',
      'This user already has this role. The same role cannot be assigned more than one time.',
    ],
    [
      'Validation error',
      'Please check the highlighted fields and try again.',
    ],
    [
      'Requested refund',
      'Requested refund amount',
    ],
    [
      'remaining refundable amount',
      'remaining refundable balance',
    ],
  ]

  let next = message.trim()
  for (const [from, to] of replacements) {
    next = next.split(from).join(to)
  }

  return next
    .replace(/^planned_start:\s*/i, '')
    .replace(/^planned_end:\s*/i, '')
    .replace(/;\s*planned_end:\s*/i, ' ')
    .replace(/;\s*planned_start:\s*/i, ' ')
    .trim()
}

function sanitizeServerMessage(message: string, status?: number): string {
  const trimmed = message.trim()
  if (!trimmed) {
    return getStatusFallbackMessage(status)
  }

  if (/<!doctype|<html|<body|<div|<p>|<li>|django/i.test(trimmed)) {
    return getStatusFallbackMessage(status)
  }

  return humanizeValidationMessage(trimmed.replace(/\s+/g, ' '))
}

// API error handler helper
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<Record<string, unknown>>
    const status = axiosError.response?.status

    if (axiosError.response?.data !== undefined) {
      const raw = extractMessageFromResponseData(axiosError.response.data)
      if (raw) {
        return sanitizeServerMessage(raw, status)
      }
    }

    if (axiosError.message) {
      return sanitizeServerMessage(axiosError.message, status)
    }

    if (status && status >= 500) {
      return getStatusFallbackMessage(status)
    }
  }

  if (error instanceof Error) return error.message
  return 'An unexpected error occurred'
}

export default api
